import { r3LabsService } from '../lib/r3dlabs';
import './EventDetailsModal.css';

interface EventDetails {
  id: string;
  display_name: string;
  description: string;
  start_datetime: string;
  end_datetime: string;
  image: {
    id: string;
    image_url: string;
  };
  slot_list: Array<{
    id: string;
    start_datetime: string;
    slot_live: boolean;
    user_channel?: {
      display_name: string;
      profile_image_url: string;
    };
  }>;
}

export class EventDetailsModal {
  private static instance: EventDetailsModal | undefined;
  private modal: HTMLElement;
  private content: HTMLElement;
  private readonly PLACEHOLDER_IMAGE = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSI0MDAiIGZpbGw9IiMxQTFBMUEiLz48cGF0aCBkPSJNMTgwIDIwMEgyMjBNMTUwIDE3MEgxNzBNMjMwIDE3MEgyNTBNMTUwIDIzMEMyMDAgMjYwIDIwMCAyNjAgMjUwIDIzMCIgc3Ryb2tlPSIjOTE0N0ZGIiBzdHJva2Utd2lkdGg9IjEwIiBzdHJva2UtbGluZWNhcD0icm91bmQiLz48L3N2Zz4=';
  private currentEvent: EventDetails | null = null;

  private constructor() {
    this.modal = document.createElement('div');
    this.modal.className = 'event-details-modal';

    this.content = document.createElement('div');
    this.content.className = 'event-details-content';
    this.modal.appendChild(this.content);

    // Close on overlay click
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.hide();
      }
    });

    document.body.appendChild(this.modal);
  }

  public static getInstance(): EventDetailsModal {
    if (!EventDetailsModal.instance) {
      EventDetailsModal.instance = new EventDetailsModal();
    }
    return EventDetailsModal.instance;
  }

  private formatDateTime(utcDateTimeStr: string): string {
    // Parse the UTC date string and create a UTC Date object
    const [dateStr, timeStr] = utcDateTimeStr.split('T');
    const [year, month, day] = dateStr.split('-').map(Number);
    const [hours, minutes] = timeStr.split(':').map(Number);
    
    const utcDate = new Date(Date.UTC(year, month - 1, day, hours, minutes));
    
    // Get timezone abbreviation
    const timeZoneName = new Intl.DateTimeFormat('en-US', {
      timeZoneName: 'short',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    }).formatToParts(utcDate).find(part => part.type === 'timeZoneName')?.value || '';

    return `${utcDate.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })} ${timeZoneName}`;
  }

  private getEventImageUrl(imageId: string | null): string {
    if (!imageId) return this.PLACEHOLDER_IMAGE;
    return `${r3LabsService.baseUrl}/images/${imageId}`;
  }

  private filterToggle: HTMLElement | null = null;
  private filterInput: HTMLInputElement | null = null;

  private handleFilterToggle = (): void => {
    const filterSection = this.modal.querySelector('.event-modal-slot-filter');
    if (filterSection && this.filterToggle && this.filterInput) {
      const isVisible = filterSection.classList.toggle('visible');
      this.filterToggle.classList.toggle('active', isVisible);
      if (!isVisible) {
        this.filterInput.value = '';
        this.filterSlots('');
      } else {
        this.filterInput.focus();
      }
    }
  };

  private handleFilterInput = (e: Event): void => {
    this.filterSlots((e.target as HTMLInputElement).value);
  };

  private setupEventListeners(): void {
    this.filterToggle = this.modal.querySelector('.event-modal-filter-toggle');
    this.filterInput = this.modal.querySelector('.event-modal-slot-input');

    if (this.filterToggle) {
      this.filterToggle.addEventListener('click', this.handleFilterToggle);
    }

    if (this.filterInput) {
      this.filterInput.addEventListener('input', this.handleFilterInput);
    }
  }

  private scrollToLiveSlot(): void {
    setTimeout(() => {
      const liveSlot = this.modal.querySelector('.event-modal-slot-item.live');
      if (liveSlot) {
        liveSlot.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }

  private filterSlots(searchTerm: string): void {
    if (!this.currentEvent) return;

    const slotsList = this.modal.querySelector('.event-modal-slots-list');
    if (!slotsList) return;

    const normalizedSearch = searchTerm.toLowerCase().trim();
    const slots = this.currentEvent.slot_list;

    slotsList.innerHTML = slots
      .filter(slot => {
        if (!normalizedSearch) return true;
        return slot.user_channel?.display_name.toLowerCase().includes(normalizedSearch);
      })
      .map(slot => `
        <div class="event-modal-slot-item ${slot.slot_live ? 'live' : ''}">
          <div class="event-modal-slot-time">
            ${this.formatDateTime(slot.start_datetime)}
          </div>
          <div class="event-modal-slot-user">
            ${slot.user_channel ? `
              <img 
                src="${slot.user_channel.profile_image_url}" 
                alt="${slot.user_channel.display_name}"
                class="event-modal-slot-user-image"
                onerror="this.src='${this.PLACEHOLDER_IMAGE}'"
              >
              <span class="event-modal-slot-user-name">${slot.user_channel.display_name}</span>
            ` : `
              <img 
                src="${this.PLACEHOLDER_IMAGE}" 
                alt="Unassigned"
                class="event-modal-slot-user-image"
              >
              <span class="event-modal-slot-user-name">Unassigned</span>
            `}
          </div>
          ${slot.slot_live ? '<div class="event-modal-live-badge">NOW</div>' : ''}
        </div>
      `).join('') || '<div class="event-modal-no-slots">No slots match your search.</div>';
  }

  public async show(eventId: string): Promise<void> {
    this.content.innerHTML = `
      <div class="event-modal-loading">
        <div class="event-modal-spinner"></div>
      </div>
    `;
    this.modal.classList.add('active');

    try {
      const eventDetails = await r3LabsService.getEventDetails(eventId);
      this.currentEvent = eventDetails;
      this.renderEventDetails(eventDetails);
      this.setupEventListeners();
      this.scrollToLiveSlot();
    } catch (error) {
      console.error('Failed to load event details:', error);
      const errorMessage = error instanceof Error 
        ? error.message.includes('404') 
          ? 'Event not found.'
          : 'Failed to load event details.'
        : 'An unexpected error occurred.';

      this.content.innerHTML = `
      <div class="event-modal-error">
        <div class="event-modal-error-message">${errorMessage}</div>
      </div>
      `;
    }
  }

  private renderEventDetails(event: EventDetails): void {
    const imageUrl = event.image?.image_url || this.getEventImageUrl(event.image?.id || null);
    
    this.content.innerHTML = `
      <button class="event-modal-close" title="Close">&times;</button>
      
      <div class="event-modal-header">
        <div class="event-modal-image-container">
          <img 
            src="${imageUrl}" 
            alt="${event.display_name}"
            class="event-modal-image"
            onerror="this.src='${this.PLACEHOLDER_IMAGE}'"
          >
        </div>
        <div class="event-modal-title-info">
          <h2 class="event-modal-title">${event.display_name}</h2>
          <div class="event-modal-time">
            <div class="time-start">${this.formatDateTime(event.start_datetime)}</div>
            <div class="time-end">${this.formatDateTime(event.end_datetime)}</div>
          </div>
        </div>
      </div>

      <div class="event-modal-description-section ${window.innerWidth >= 768 ? '' : 'collapsed'}">
        <div class="event-modal-description-header">
          <h3 class="event-modal-description-title">Description</h3>
          <button class="event-modal-description-toggle" aria-label="Toggle description">
            <svg width="16" height="16" viewBox="0 0 16 16">
              <path fill="currentColor" d="M3.47 5.47a.75.75 0 0 1 1.06 0L8 8.94l3.47-3.47a.75.75 0 1 1 1.06 1.06l-4 4a.75.75 0 0 1-1.06 0l-4-4a.75.75 0 0 1 0-1.06z"/>
            </svg>
          </button>
        </div>
        <div class="event-modal-description">${event.description || 'No description available.'}</div>
      </div>

      <div class="event-modal-schedule-section">
        <div class="event-modal-slots-header">
          <h3 class="event-modal-section-title">Event Schedule</h3>
          <button class="event-modal-filter-toggle" aria-label="Toggle search filter">
            <svg class="event-modal-filter-icon" viewBox="0 0 24 24" width="16" height="16">
              <path fill="currentColor" d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
            </svg>
          </button>
        </div>

        <div class="event-modal-slot-filter">
          <input 
            type="text" 
            class="event-modal-slot-input" 
            placeholder="Search by username..."
          >
        </div>
      </div>

      <div class="event-modal-scrollable-content">
        <div class="event-modal-slots-list">
          ${event.slot_list?.map(slot => `
            <div class="event-modal-slot-item ${slot.slot_live ? 'live' : ''}">
              <div class="event-modal-slot-time">
                ${this.formatDateTime(slot.start_datetime)}
              </div>
              <div class="event-modal-slot-user">
                ${slot.user_channel ? `
                  <img 
                    src="${slot.user_channel.profile_image_url}" 
                    alt="${slot.user_channel.display_name}"
                    class="event-modal-slot-user-image"
                    onerror="this.src='${this.PLACEHOLDER_IMAGE}'"
                  >
                  <span class="event-modal-slot-user-name">${slot.user_channel.display_name}</span>
                ` : `
                  <img 
                    src="${this.PLACEHOLDER_IMAGE}" 
                    alt="Unassigned"
                    class="event-modal-slot-user-image"
                  >
                  <span class="event-modal-slot-user-name">Unassigned</span>
                `}
              </div>
              ${slot.slot_live ? '<div class="event-modal-live-badge">NOW</div>' : ''}
            </div>
          `).join('') || '<div class="event-modal-no-slots">No slots available for this event.</div>'}
        </div>
      </div>

      <div class="event-modal-footer">
        <button class="event-modal-close-button" title="Close">Close</button>
      </div>
    `;

    // Add click handlers for close buttons and description toggle
    const closeButtons = this.content.querySelectorAll('.event-modal-close, .event-modal-close-button');
    closeButtons.forEach(button => {
      button.addEventListener('click', () => this.hide());
    });

    // Add description toggle handler
    const descriptionHeader = this.content.querySelector('.event-modal-description-header');
    const descriptionToggle = this.content.querySelector('.event-modal-description-toggle');
    const descriptionSection = this.content.querySelector('.event-modal-description-section');
    
    const toggleDescription = (e: Event) => {
      e.stopPropagation(); // Prevent event bubbling
      if (descriptionSection) {
        if (descriptionSection.classList.contains('collapsed')) {
          descriptionSection.classList.remove('collapsed');
        } else {
          descriptionSection.classList.add('collapsed');
        }
      }
    };

    if (descriptionHeader) {
      descriptionHeader.addEventListener('click', toggleDescription);
    }
    if (descriptionToggle) {
      descriptionToggle.addEventListener('click', toggleDescription);
    }
  }

  public hide(): void {
    this.modal.classList.remove('active');
  }

  public destroy(): void {
    // Remove all event listeners
    if (this.filterToggle) {
      this.filterToggle.removeEventListener('click', this.handleFilterToggle);
      this.filterToggle = null;
    }
    
    if (this.filterInput) {
      this.filterInput.removeEventListener('input', this.handleFilterInput);
      this.filterInput = null;
    }

    // Remove close button listeners
    const closeButtons = this.content.querySelectorAll('.event-modal-close, .event-modal-close-button');
    closeButtons.forEach(button => {
      button.removeEventListener('click', () => this.hide());
    });
    
    // Remove the modal from DOM
    this.modal.remove();
    
    // Clear any stored state
    this.currentEvent = null;
    EventDetailsModal.instance = undefined;
  }
}

// Export singleton instance
export const eventDetailsModal = EventDetailsModal.getInstance();
